import { Clock3 } from 'lucide-react';

import type { AutosaveState, EditorKind } from './ClinicalEditorShell';

interface Props {
  kind: EditorKind;
  clientName: string;
  scheduledStart: string | null;
  authorLabel: string;
  autosaveState: AutosaveState;
  wordCount: number;
}

const SAVE_LABEL: Record<AutosaveState, string> = {
  idle: 'Up to date',
  dirty: 'Unsaved',
  saving: 'Saving…',
  saved: 'Saved',
  failed: 'Save failed',
};

const SAVE_TONE: Record<AutosaveState, string> = {
  idle: 'bg-primary-400',
  dirty: 'bg-warning-500',
  saving: 'bg-primary-300 animate-pulse',
  saved: 'bg-primary-400',
  failed: 'bg-error-500',
};

const formatLongDate = (iso: string | null): string => {
  if (iso === null) return '';
  const d = new Date(iso);
  const weekday = d.toLocaleDateString(undefined, { weekday: 'long' });
  const date = d.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${weekday}, ${date} · ${time}`;
};

export function EditorDocumentHeader({
  kind,
  clientName,
  scheduledStart,
  authorLabel,
  autosaveState,
  wordCount,
}: Props) {
  const eyebrow = kind === 'note' ? 'Session notes' : 'Case report';
  // The eyebrow already names the document kind, so the title is just the
  // client name dressed in editorial italic — reads like the letterhead of a
  // printed monograph rather than a redundant "Case report · Case report".
  const title = kind === 'note' ? `Notes for ${clientName}` : `For ${clientName}`;
  const longDate = formatLongDate(scheduledStart);
  const saveLabel = SAVE_LABEL[autosaveState];
  const saveTone = SAVE_TONE[autosaveState];

  return (
    <header className="border-accent-ink/10 mb-8 border-b pb-6">
      <p className="font-display tracking-eyebrow text-accent-ink mb-3 text-[11px] font-medium uppercase italic">
        {eyebrow}
      </p>
      <h1 className="font-display text-accent-ink text-[34px] leading-[1.15] font-medium italic">
        {title}
      </h1>
      <div className="text-meta-sm mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-neutral-500">
        {longDate !== '' ? (
          <span className="inline-flex items-center gap-1.5 font-mono">
            <Clock3 className="h-3 w-3" strokeWidth={1.5} aria-hidden />
            {longDate}
          </span>
        ) : null}
        {longDate !== '' ? <span aria-hidden>·</span> : null}
        <span className="font-mono">{`${String(wordCount)} words`}</span>
        {authorLabel !== '' ? (
          <>
            <span aria-hidden>·</span>
            <span>
              by <span className="text-accent-ink/80">{authorLabel}</span>
            </span>
          </>
        ) : null}
        <span aria-hidden>·</span>
        <output className="inline-flex items-center gap-1.5">
          <span className={`h-1.5 w-1.5 rounded-full ${saveTone}`} aria-hidden />
          {saveLabel}
        </output>
      </div>
    </header>
  );
}
